import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useState, useMemo } from 'react';

// DATA
import { accountsSelectors } from 'app-components/flux/accounts';
import { billPresentmentActions } from 'app-components/flux/bill-presentment';
import { billerAccountsActions } from 'app-components/flux/biller-accounts';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { scheduledTransactionsSelectors, scheduledTransactionsUtils } from 'app-components/flux/scheduled-transactions';
import { transactionsSelectors, transactionsTypes, transactionsUtils } from 'app-components/flux/transactions';
import { tracker } from 'app-components/utils/core';
import useTxnActions from 'data/transactionActions/useTxnActions';

import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import { rootUiActions } from 'app-components/flux/root-ui';
import { showMatchTxnDialog } from 'components/Dialogs/MatchTxnDialog/actions';

import ConnectedAvatar from 'components/ConnectedAvatar';
import AlertDialog from 'components/Dialogs/AlertDialog';
import StdDialog from 'components/Dialogs/StdDialog';

import { dateSelectors } from 'app-components/flux/date';
import QAmountField from 'components/QAmountField';
// CUSTOM
import QButton from 'components/QButton';
import AccountField from 'components/QuickenControls/AccountField';
import CategoryField from 'components/QuickenControls/CategoryField';
import SPayeeField from 'components/SPayeeField';
import DateField from 'components/Transactions/DateField';
import VisibilitySection from 'components/Transactions/VisibilitySection';

import useScheduledTransactionEditDialog from 'components/ScheduledTransactions/ScheduledTransactionEditDialog/dispatcher';
import BillStatus from './BillStatus';

const useStyles = makeStyles()((theme) => ({
  content: {
    maxWidth: 600,
    padding: 24,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  leftSide: {
    width: 80,
    marginRight: 32,
  },
  rightSide: {
    flexGrow: 2,
    minWidth: 420,
    paddingRight: 32,
    display: 'flex',
    flexDirection: 'column',
  },
  row: {
    display: 'flex',
  },
  payee: {
    width: '100%',
    paddingBottom: 8,
  },
  amtTxt: {
    width: '68%',
    textAlign: 'left',
    paddingRight: 16,
  },
  manualFields: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    paddingRight: 80,
    paddingBottom: 12,
  },
  linkRow: {
    margin: '8px 0 12px',
  },
  divider: {
    width: '100%',
    height: 1,
    backgroundColor: theme.palette.divider,
    marginBottom: 16,
  },
  actions: {
    width: '100%',
    padding: 24,
    position: 'relative',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  leftButton: {
    position: 'absolute',
    left: 24,
  },
}));
const buttonMargin = { marginLeft: 8 };

interface EditReminderDialogProps {
  txn: transactionsTypes.TransactionRecord;
  context?: string;
  dialogId?: string;
}

// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
// ### - - -                  Dialog Component                   - - - ###
// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
function EditReminderDialog(props: EditReminderDialogProps) {
  const { classes } = useStyles();
  const showScheduledTransactionDialog = useScheduledTransactionEditDialog();
  const { txn, context = '', dialogId } = props;
  const initialAmount = txn?.amount?.toFixed(2) || '0.00';

  const { saveTransactions, deleteTransactions } = useTxnActions({ wrapperId: context });

  const dispatch = useAppDispatch();
  const source =
    window.location.pathname === '/'
      ? 'dashboard'
      : window.location.pathname.replaceAll?.(/[/-]|(\d+)/g, (match) =>
          match === '/' ? '' : match === '-' ? ' ' : 'xxx',
        );

  const fiServ = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'billsFiserv'));

  useEffect(() => {
    dispatch(billerAccountsActions.getBillerAccounts());
    if (fiServ) {
      dispatch(billPresentmentActions.fetch());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isPendingScheduledTxn = txn.source === 'SCHEDULED_TRANSACTION_PENDING';
  const futureTxnsFf = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'futureTxns'));

  // +++     State values     +++
  const [amount, setAmount] = useState(initialAmount);
  const [date, setDate] = useState(DateTime.fromISO(txn.postedOn));
  const [dateError, setDateError] = useState('');
  const [excludeReports, setExcludeReports] = useState(Boolean(txn.isExcludedFromReports));
  const [excludeF2S, setExcludeF2S] = useState(Boolean(txn.isExcludedFromF2S));

  const [edit, setEdit] = useState(false);
  const [dirtyDialog, setDirtyDialog] = useState(false);
  const [payee, setPayee] = useState(txn.payee);
  const [coa, setCoa] = useState(txn.coa);
  const [accountId, setAccount] = useState(txn.accountId);

  const model = useAppSelector((state) =>
    scheduledTransactionsSelectors.getScheduledTransactionById(state, txn?.stModelId || ''),
  );

  const currentDate = useAppSelector(dateSelectors.getCurrentDate);
  const yearFromCurrentDate = currentDate.plus({ year: 1 });

  const dateInfoText = useMemo(() => {
    if (!futureTxnsFf) {
      return null;
    }

    if (date > currentDate) {
      return 'Future dates will create a Reminder.';
    }

    return null;
  }, [currentDate, date, futureTxnsFf]);

  const scheduledTxnType = model && scheduledTransactionsUtils.typeFromScheduledTransaction(model)?.toLowerCase();

  const accounts = useAppSelector(accountsSelectors.getAccountsById);
  const accountName = accounts?.get(txn?.accountId || '')?.name;
  const isDirty =
    amount !== initialAmount ||
    date.toISODate() !== txn.postedOn ||
    excludeReports !== Boolean(txn.isExcludedFromReports) ||
    excludeF2S !== Boolean(txn.isExcludedFromF2S) ||
    txn.coa !== coa ||
    txn.accountId !== accountId ||
    txn.payee !== payee;

  const isIncome = useAppSelector((state) => transactionsSelectors.isIncomeTxn(state, txn));

  //  TODO: Move disable to shared component in app-components DISABLEREPORTS
  const disableReports = coa?.type === 'BALANCE_ADJUSTMENT';

  const clearDialog = useCallback(() => {
    dispatch(rootUiActions.removeDialog(dialogId));
  }, [dialogId, dispatch]);

  const onSave = useCallback(() => {
    if (dateError) {
      return;
    }

    const txnToSave = transactionsTypes.mkCashFlowTransaction({
      ...txn.toJS(),
      isExcludedFromF2S: excludeF2S,
      isExcludedFromReports: excludeReports,
      postedOn: date.toISODate() || txn.postedOn,
      amount: Number(Number(amount).toFixed(2)),
      tags: txn.tags,
      coa,
      accountId,
      payee,
    } as Partial<transactionsTypes.TransactionProps>);
    saveTransactions([txnToSave]);

    tracker.track(tracker.events.changeReminder, {
      type: scheduledTxnType || 'other',
      source: source,
      excludeFromSpendingPlan: excludeF2S,
      excludeFromReports: excludeReports,
    });

    clearDialog();
  }, [
    dateError,
    txn,
    excludeF2S,
    excludeReports,
    date,
    amount,
    coa,
    accountId,
    payee,
    saveTransactions,
    clearDialog,
    scheduledTxnType,
    source,
  ]);

  const openEdit = () => {
    clearDialog();
    return (
      model &&
      showScheduledTransactionDialog({
        open: true,
        scheduledTransaction: model,
      })
    );
  };

  const closeCheck = (e?: unknown, reason?: string) => {
    if (isDirty && !dateError) {
      if (reason === 'ENTER_KEY_DOWN') {
        onSave();
      } else {
        setDirtyDialog(true);
      }
    }
  };

  const closeAndLink = () => {
    closeCheck();
    clearDialog();
    dispatch(showMatchTxnDialog({ txn, wrapperId: context }));
  };

  const amountChange = (e: any) => setAmount(e.target.value);

  const onDelete = () => {
    deleteTransactions([txn]);
    clearDialog();
  };

  const onHandleClose = useCallback(
    (e?: any, reason?: string) => {
      if (reason === 'SUBMIT_BUTTON' || reason === 'ENTER_KEY_DOWN') {
        // update
        onSave();
      }
      clearDialog();
    },
    [clearDialog, onSave],
  );

  return (
    <>
      <StdDialog
        onClose={onHandleClose}
        open={!edit}
        sharedcomponentid='EDIT_REMINDER_DIALOG'
        size='sm'
        title={isPendingScheduledTxn ? 'Edit reminder' : 'Edit transaction'}
      >
        <div className={classes.content}>
          <div className={classes.leftSide}>
            <ConnectedAvatar model={model} txn={txn} size={64} iconSize={24} />
          </div>

          <div className={classes.rightSide}>
            {isPendingScheduledTxn ? (
              <div className={classes.payee}>
                <Typography variant='displayRegular' noWrap>
                  {txn.payee}
                </Typography>
                <Typography variant='bodyRegular'>{accountName}</Typography>
              </div>
            ) : (
              <SPayeeField defaultValue={payee} onChange={setPayee} id='edit-reminder-payee-field' autoFocus />
            )}

            <div className={classes.row}>
              <QAmountField
                value={amount}
                onChange={amountChange}
                expectedSign={isIncome ? 'positive' : 'negative'}
                fullWidth
                editable
                label='Amount'
                id='Reminder_Amount'
                margin='normal'
                className={classes.amtTxt}
              />

              {/* Date Field */}
              <DateField
                maxDate={futureTxnsFf ? yearFromCurrentDate : undefined}
                date={date}
                onChange={(date: DateTime) => {
                  if (futureTxnsFf && date > yearFromCurrentDate) {
                    setDateError(`Date must be before ${yearFromCurrentDate.toFormat('MM/dd/yyyy')}`);
                  } else {
                    setDateError('');
                  }

                  setDate(date);
                }}
                error={!!dateError}
                helperText={dateError || dateInfoText}
              />
            </div>

            <BillStatus txn={txn} model={model} onClose={clearDialog} />

            {/* manual transaction fields */}
            {!isPendingScheduledTxn && (
              <div className={classes.manualFields}>
                <AccountField
                  initialAccountId={accountId}
                  onSelected={(selectedAccount?: any) => setAccount(selectedAccount?.id)}
                  label='Account'
                  variant='filled'
                  margin='normal'
                  fullWidth
                />

                {!transactionsUtils.isSplitTxn(txn) && (
                  <CategoryField
                    variant='bodyRegular'
                    id={txn.id}
                    editable
                    value={coa}
                    label='Category'
                    onChange={(txnCoa: any) => setCoa(txnCoa)}
                    textFieldVariant='filled'
                    margin='normal'
                  />
                )}
              </div>
            )}

            {/* link option */}
            {isPendingScheduledTxn && (
              <Typography variant='paragraphRegular' className={classes.linkRow}>
                Already {isIncome ? 'received' : 'paid'}?
                <QButton type='button' id='link-button' aria-label='link-transaction' onClick={closeAndLink}>
                  LINK TRANSACTION
                </QButton>
              </Typography>
            )}

            <div className={classes.divider} />

            {/* Ignore Checkboxes */}
            <VisibilitySection
              disabledReports={disableReports}
              excludeF2S={excludeF2S}
              setExcludeF2S={setExcludeF2S}
              excludeReports={excludeReports}
              setExcludeReports={setExcludeReports}
            />
          </div>
        </div>

        <div className={classes.actions}>
          {isPendingScheduledTxn && model && (
            <QButton
              type='button'
              id='edit-series'
              aria-label='edit-series'
              onClick={openEdit}
              className={classes.leftButton}
            >
              Edit Series
            </QButton>
          )}

          <QButton
            type='button'
            id='delete-reminder'
            aria-label='delete-reminder'
            onClick={onDelete}
            disabled={!txn || !txn.id}
            variant='delete'
            disableOutline
          >
            Delete
          </QButton>

          <QButton
            type='submit'
            id='create-update-recurrence'
            variant='contained'
            disabled={!isDirty || !!dateError}
            onClick={onSave}
            style={buttonMargin}
          >
            Update
          </QButton>
        </div>
      </StdDialog>

      {dirtyDialog && (
        <AlertDialog
          dialogId='ignore this warning'
          title='Just checking...'
          content='Are you sure you want to discard all changes?'
          onClose={(btnObj: { btnPressed: string }) => {
            setDirtyDialog(false);
            if (btnObj.btnPressed === 'Discard') {
              if (edit) {
                setEdit(false);
              }
            }
          }}
          buttons={['Go Back', 'Discard']}
        />
      )}
    </>
  );
}

EditReminderDialog.displayName = 'EditReminderDialog';

export default EditReminderDialog;
